import { useContext, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import type { NavItem } from '../../lib/navigation'
import { idOngletDePage, ongletCourant } from '../../lib/ongletsDePage'
import { OngletsDeLaPageContext } from '../../contexts/OngletsDeLaPageContext'
import { OngletsDePage } from './OngletsDePage'

/**
 * Une barre d'onglets qui garde le focus d'une barre à l'autre : chaque page pose la sienne, et la page qui se charge
 * remplace celle de son squelette. Si la barre démontée avait le focus, la suivante le rend à l'onglet ouvert, comme le
 * faisait la barre unique de la coquille (revue à froid du second retour, 2026-10-12).
 */
export function BarreOngletsSuivie({ entree, idPanneau, dansLaPage }: { entree: NavItem; idPanneau: string; dansLaPage: boolean }) {
  const { focusAReprendre } = useContext(OngletsDeLaPageContext)
  const { pathname } = useLocation()
  const barre = useRef<HTMLDivElement>(null)
  const courant = ongletCourant(entree, pathname)
  const idCourant = courant ? idOngletDePage(entree, courant) : null

  useLayoutEffect(() => {
    if (focusAReprendre.current && idCourant) {
      focusAReprendre.current = false
      document.getElementById(idCourant)?.focus()
    }
    const element = barre.current
    return () => {
      if (element && element.contains(document.activeElement)) focusAReprendre.current = true
    }
  }, [focusAReprendre, idCourant])

  return <OngletsDePage entree={entree} idPanneau={idPanneau} dansLaPage={dansLaPage} refBarre={barre} />
}

/**
 * Spec 161, second retour de Christophe sur `dev` (2026-10-12) — à poser par une page réunie sous une entrée de menu
 * (« Licences et économies », « Analyses »…) **juste sous son en-tête** : titre, aide, actions. Rend la barre d'onglets
 * de l'entrée, comme la coquille la rendait en tête de page, et prévient la coquille qu'elle n'a plus à la poser.
 *
 * Hors d'une entrée à onglets, ne rend rien : une page peut la poser sans savoir sous quelle entrée elle est rangée.
 * Le signal part dans un effet de mise en page, avant l'affichage : la barre ne passe jamais par le haut de la page.
 */
export function OngletsDeLEntree() {
  const { entree, idPanneau, signaler } = useContext(OngletsDeLaPageContext)
  const actif = entree !== null
  useLayoutEffect(() => {
    if (!actif) return
    signaler(1)
    return () => signaler(-1)
  }, [actif, signaler])
  if (!entree) return null
  return <BarreOngletsSuivie entree={entree} idPanneau={idPanneau} dansLaPage />
}
